import { useRef, useState } from "react"
import { Box, List, ListItem, Paper, Popper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, useAutocomplete, useTheme, Typography } from "@mui/material";
import { t } from "i18next";
import { IVatGuiModel } from "../../../model/IVatGuiModel";
import { matchSorter } from "match-sorter";

interface VatSearchProps {
    vat: IVatGuiModel | null,
    handleUpdate: (type: "debetVat" | "creditVat", vat: IVatGuiModel | null, error: boolean) => void,
    handleInputOnEnter: () => void,
    type: "debetVat" | "creditVat",
    vatList: IVatGuiModel[],
    vatListError: string[],
    vatLoader: boolean,
}

const DialogVoucherRegistrationTableVatSearch = (props: VatSearchProps) => {

    const theme = useTheme();

    const { vat, handleUpdate, handleInputOnEnter, type, vatList, vatListError, vatLoader } = props;

    const [vatError, setVatError] = useState("");
    const handleVatError = (error: string) => {

        setVatError(error);
    }

    const highligthedOption = useRef<IVatGuiModel | null>(null);

    const {
        getRootProps,
        getInputProps,
        getListboxProps,
        getOptionProps,
        groupedOptions,
        inputValue,
    } = useAutocomplete({
        id: 'vat-autocomplete',
        options: vatList,
        value: vat,
        isOptionEqualToValue: (option, value) => value && option.vatCodeId === value.vatCodeId ? true : false,
        onChange: (event: any, newValue: IVatGuiModel | null) => {

            if (newValue) {
                handleUpdate(type, newValue, false);
                handleVatError("");
            } else {
                handleUpdate(type, null, true);
                handleVatError("X");
            }
        },
        onHighlightChange: (event, option, reason) => {
            highligthedOption.current = option;
        },
        filterOptions: (options, { inputValue }) => matchSorter(options, inputValue, { keys: ["vatCodeId", "name"] }),
        selectOnFocus: true,
        autoSelect: true,
        openOnFocus: true,
        onClose: (event) => {
            setAnchorEl(null);
            if (!highligthedOption.current) {
                let newValueFromInput: IVatGuiModel | undefined;
                if ((newValueFromInput = vatList.find(e => e.vatCodeId === inputValue))) {
                    handleUpdate(type, newValueFromInput, false);
                    handleVatError("");
                } else if (inputValue === "") {
                    handleUpdate(type, null, false);
                    handleVatError("");
                } else {
                    handleUpdate(type, null, true);
                    handleVatError(inputValue);
                }
            }
        },
        getOptionLabel: (option) => option.vatCodeId + " - " + option.rate + "%",
    });

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    return <div {...getRootProps()}>
        <TextField
            label={vatError ? vatError + " - " + t("basic.doesNotExist") : vat?.name}
            size="small"
            variant="filled"
            error={vatError !== "" ? true : false}
            onFocus={(e) => setAnchorEl(e.currentTarget)}
            onKeyUp={(e) => {
                if (e.key === "Enter") {
                    handleInputOnEnter();
                }
            }}
            inputProps={{ ...getInputProps() }}
            InputProps={{ fullWidth: true, disableUnderline: true }}
            sx={{
                minWidth: "6rem", "& .MuiInputBase-root": { height: "2.5rem" },
                "& .MuiFilledInput-root": { backgroundColor: theme.palette.background.paper, },
                border: vatError ? 2 : 0,
                borderColor: "red"
            }}
            fullWidth />
        <Popper sx={{ zIndex: 2000, }} open={Boolean(anchorEl)} anchorEl={anchorEl} placement="bottom-start" onMouseDown={event => event.preventDefault()}>
            <Box component={Paper} sx={{ border: 1, borderColor: 'divider' }} elevation={10}>
                <Stack direction="row" justifyContent="space-between" sx={{margin: theme.spacing(2)}}>
                    <Typography variant="button">{t("basic.vatSearch")}</Typography>
                </Stack>
                <TableContainer>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: "9rem" }}>{t("basic.vatId")}</TableCell>
                                <TableCell sx={{ width: "20rem" }}>{t("basic.name")}</TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </TableContainer>
                <TableContainer component={List} {...getListboxProps()} disablePadding
                    sx={{ maxHeight: "15rem", }}>
                    <Table size="small">
                        <TableBody role="listbox">
                            {(groupedOptions as typeof vatList).map((option, index) => (
                                <TableRow component={ListItem} {...getOptionProps({ option, index })} key={index} sx={{ zIndex: theme.zIndex.appBar + 1 }}>
                                    <TableCell sx={{ width: "9rem" }}>
                                        {option.vatCodeId}
                                    </TableCell>
                                    <TableCell sx={{ width: "20rem" }}>
                                        {option.name}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Popper>
    </div>
}
export default DialogVoucherRegistrationTableVatSearch;